import { Component, OnInit } from '@angular/core';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import { isNullOrUndefined } from 'util';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ROUTES } from '@app/clpbp-app.settings';

import { UnityBranchOffice } from '@shared/models';
import {
  ApplicationTypeEnum,
  SessionService
} from '@shared/services/session';
import {
  AuthService,
  BorrowerService,
  DashboardService,
  FeatureToggleService,
  MixpanelService
} from '@shared/services';
import { PLApplicationProgressService } from '@personal-loans/shared';
import { FinicityService } from '@app/shared/services/finicity/finicity.service';

@Component({
  selector: 'ld-header',
  templateUrl: './header.component.html',
  styleUrls: [ './header.style.scss' ]
})
export class HeaderComponent implements OnInit {
  public applicationType: ApplicationTypeEnum;
  public branchOffice: UnityBranchOffice;
  public isLoggedIn = false;

  public isDashboardEnabled = false;
  public isOnDashboard = false;
  public isOnLoginPage = false;
  public isOnPersonalPortal = false;
  public isOnUpdatePassword = false;
  public showMobileMenu = false;
  public showSectionDetail = false;

  private _currentUser: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private isMultiBorrower = false;
  private nameUpdatingSubscriptions: Subscription[] = [];

  public _isProfileMenuVisible: boolean;
  public willHideProfileMenu: number;

  public set isProfileMenuVisible(to: boolean) {
    // always clear the timeout
    // if willHide is null it'll fail silently in all browsers
    clearTimeout(this.willHideProfileMenu);
    this._isProfileMenuVisible = to;
  }

  public get isProfileMenuVisible(): boolean {
    return this._isProfileMenuVisible;
  }

  public get phoneNumber(): string {
    // Returns the phone number outside of either PL or mortgage applications.
    if (
      this.applicationType === ApplicationTypeEnum.Global
      || this.applicationType === ApplicationTypeEnum.Dashboard
      || this.applicationType === ApplicationTypeEnum.Login
    ) {
      return '(855) 414-3319';
    }

    // This returns personal loan phone number
    return '(800) 656-2606';
  }

  public get currentUser(): string {
    return this._currentUser.getValue();
  }

  constructor(
    private plApplicationProgressService: PLApplicationProgressService,
    private authService: AuthService,
    private borrowerService: BorrowerService,
    private dashboardService: DashboardService,
    private features: FeatureToggleService,
    private finicityService: FinicityService,
    public mixpanelService: MixpanelService,
    private router: Router,
    private sessionService: SessionService
  ) {

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.refreshProfile();
      }

      if (event instanceof NavigationEnd) {
        this.isOnDashboard = event.url.indexOf(ROUTES.DASHBOARD_FULL) > -1;
        this.isOnLoginPage = (
          event.url.indexOf(ROUTES.LOGIN_FULL) > -1
          || event.url.indexOf(ROUTES.SIGNIN_FULL) > -1
        );
        this.isOnPersonalPortal = event.url.search(/personal-loans\/(todos|messages|documents|contact)/) !== -1;
        this.isOnUpdatePassword = event.url.indexOf(`/${ ROUTES.APP_BASE_SLASH }update-password`) > -1;
      }
    });

    this.features.isUnifiedLoginEnabled.subscribe(
      value => this.isDashboardEnabled = value,
      error => console.error(error)
    );

    this.refreshProfile();
  }

  ngOnInit(): void {
    this.sessionService.logout = this.logout;

    this.dashboardService.isMultiBorrowerAccount.subscribe(this.updateMultiBorrowerState, console.error);
    this.sessionService.onApplicationTypeChange().subscribe(this.handleApplicationTypeChange, console.error);
    this.isMultiBorrower = undefined;

    const { borrowerData } = this.sessionService.mortgageSession.data;
    borrowerData.primaryBorrowerSelected = false;

    // Essential for registering a non spouse co-borrower, who logs in before setting the primary borrower ID.
    this.updateNameSubscriptions();
    this.isProfileMenuVisible = false;
  }

  private updateNameSubscriptions(): void {
    this.nameUpdatingSubscriptions = [];
    const { authData } = this.sessionService.data;
    const { primaryBorrowerData } = this.sessionService.mortgageSession.data.borrowerData;

    this.nameUpdatingSubscriptions.push(authData.authenticated.subscribe(this.updateAccountNameDisplay, console.error));
    this.nameUpdatingSubscriptions.push(primaryBorrowerData.onBorrowerIdAssignment.subscribe(this.updateAccountNameDisplay, console.error));
  }

  private get doesMortgageOrPLGuidExist(): boolean {
    return !!this.sessionService.personalLoanSession.data.borrowerData.borrowerGuid ||
           !!this.sessionService.mortgageSession.data.borrowerData.primaryBorrowerData.borrowerId;
  }

  updateAccountNameDisplay = () => {
    if (this.sessionService.data.authData.token && this.doesMortgageOrPLGuidExist) {
      this.isLoggedIn = true;

      this.borrowerService.getUnifiedBorrower().subscribe(data => {
        const bData = this.sessionService.mortgageSession.data.borrowerData;
        const pbData = bData.primaryBorrowerData;
        pbData.firstName = data.firstName;
        pbData.lastName = data.lastName;

        const setAccountNameDisplay = (name: string) => this._currentUser.next(name);

        if (this.isMultiBorrower && !bData.primaryBorrowerSelected) {
          bData.primaryBorrowerSelected = false;
          setAccountNameDisplay('My Account');
        } else {
          setAccountNameDisplay(`${pbData.firstName} ${pbData.lastName}`);
        }
      });
    }
  }

  updateMultiBorrowerState = (isAMultiBorrowerAccount: boolean) => {
    this.isMultiBorrower = isAMultiBorrowerAccount;
  }

  handleApplicationTypeChange = (type: ApplicationTypeEnum) => {
    this.applicationType = type;

    this.refreshProfile();
  }

  refreshProfile(): void {
    if (this.sessionService && this.sessionService.data) {
      this.isLoggedIn = this.sessionService.borrowerGuid !== '';
    } else {
      this.isLoggedIn = false;
    }

    const setName = () => {
      const { firstName, lastName } = this.sessionService.mortgageSession.data.borrowerData.primaryBorrowerData;
      this._currentUser.next(firstName + ' ' + lastName);
    };

    if (this.isLoggedIn) {
      if (this.isMultiBorrower) {
        if (this.sessionService.mortgageSession.data.borrowerData.primaryBorrowerSelected) {
          setName();
        } else {
          this._currentUser.next('My Account');
        }
      } else {
        setName();
      }
    }
  }

  public isPersonalLoans(): boolean {
    return (this.router.url.indexOf('personal-loans') > -1);
  }

  login(): void {
    this.router.navigate([ ROUTES.APP_BASE_SLASH + 'login' ]);
  }

  timeoutWarning: Function = () => { };

  logout: () => void = () => {
    // Log analytics
    if (this.isOnDashboard) {
      const analyticsObj = {
        'Definition': 'Sign Out selected on Dashboard',
        'Product': 'U',
        'User': 'B',
        'Experience': 'E'
      };
      this.mixpanelService.track('Logged Out UBE', analyticsObj);
    }

    this.finicityService.closeBankLinking();
    this.sessionService.params.token.next(null);
    this.sessionService.mortgageSession.data.borrowerData.primaryBorrowerSelected = false;
    this.isMultiBorrower = false;
    this.authService.logout();
    this.dashboardService.clearCache();
    this.isProfileMenuVisible = false;
    this.showMobileMenu = false;
    this.refreshProfile();
    this.plApplicationProgressService.reset();
  }

  changePassword(): void {
    this.showMobileMenu = false;
    this.isProfileMenuVisible = false;
    // CLPBP-34 password handled by ULA dashboard
    this.sessionService.navigateToUla('update-password').then(redirected => {
      if (!redirected) {
        // this means CLPBP is hosting the dashboard/login functionality
        const page = '/' + ROUTES.APP_BASE_SLASH;
        this.router.navigate([page + 'update-password']);
      }
    });
  }

  toggleSidePanel(showSidePanel?: boolean): void {
    if (isNullOrUndefined(showSidePanel)) {
      this.showMobileMenu = !this.showMobileMenu;
    } else {
      this.showMobileMenu = showSidePanel;
    }
  }

   toggleSection(): void {
    this.showSectionDetail = !this.showSectionDetail;
  }

  navigateTo(path: string, event: Event): void {
    this.router.navigate([path]);
  }

  hideProfileMenu(): void {
    const setTimeout = window.setTimeout;
    this.willHideProfileMenu = setTimeout(() => {
      this.isProfileMenuVisible = false;
    }, 500);
  }

  showProfileMenu(): void {
    // this setter will always clearTimeout that is set in hideProfileMenu
    this.isProfileMenuVisible = true;
  }
}
